import { fmt } from '../render/escape.js';
const FIELDS = ['x', 'y', 'scale', 'rotate'];
/**
 * Wie eine Bewegung von einem Keyframe zum nächsten verläuft (S30). Sascha:
 * „Ein- und Ausschwingen statt gleichförmig.“
 *
 * **Weich war bisher das Einzige.** Jede Strecke lief durch `ease()`, den
 * Smoothstep -- und das ist für fast alles richtig. Was fehlte, ist die
 * Gegenrichtung: eine *gleichförmige* Fahrt gab es nicht. Ein Abspann, der
 * gleichmässig durchläuft, und eine langsame Kamerafahrt, die nicht am Anfang
 * und Ende bremst, waren nicht einstellbar.
 *
 * Die Kurve gehört dem Keyframe, an dem die Strecke **anfängt** -- nicht
 * beiden Enden. Ein Griff je Seite wäre die ehrlichere Nachbildung einer
 * Kurvenzeichnung, aber am Telefon zieht daran niemand; vier Möglichkeiten,
 * die man antippt, sind hier mehr wert als eine Zeichnung, die man nicht
 * trifft.
 */
/**
 * Der Wert der Kurve bei `u` (0 bis 1).
 *
 * **Diese eine Stelle und die daneben sind dasselbe.** Die Vorschau rechnet
 * hier, der Export rechnet mit `kurveAusdruck` -- und eine Prüfung vergleicht
 * beide an hundert Stellen. Der Smoothstep stand bis zum 17. September zweimal
 * im Quelltext, einmal als JavaScript und einmal als Zeichenkette für ffmpeg;
 * zwei Formeln, die dasselbe tun sollen, tun es irgendwann nicht mehr.
 */
export function kurveWert(kurve, u) {
    const c = Math.min(1, Math.max(0, u));
    switch (kurve) {
        case 'linear':
            return c;
        // Langsam los: quadratisch von null weg.
        case 'rein':
            return c * c;
        // Langsam an: quadratisch in den Halt hinein.
        case 'raus':
            return c * (2 - c);
        case 'weich':
        default:
            return c * c * (3 - 2 * c);
    }
}
/** Dieselbe Kurve als ffmpeg-Ausdruck über einem schon auf 0..1 geklemmten `u`. */
export function kurveAusdruck(kurve, u) {
    switch (kurve) {
        case 'linear':
            return `(${u})`;
        case 'rein':
            return `(${u}*${u})`;
        case 'raus':
            return `(${u}*(2-${u}))`;
        case 'weich':
        default:
            return `(${u}*${u}*(3-2*${u}))`;
    }
}
/** Ease in and out: the smoothstep everybody's eye reads as "natural". */
export function ease(u) {
    return kurveWert('weich', u);
}
function sorted(clip) {
    return [...clip.keyframes].sort((a, b) => a.at - b.at);
}
/**
 * Die kürzere von zwei Drehungen. Von 350° auf 10° sind es +20°, nicht −340°.
 *
 * Ohne das dreht sich eine Ebene fast einmal um sich selbst, wenn man sie nur
 * ein wenig gegen den Uhrzeigersinn kippt: der zweite Keyframe steht dann bei
 * 357°, und zwischen 0 und 357 liegt für eine geradlinige Rechnung der lange
 * Weg.
 */
export function shortestTurn(from, to) {
    return ((((to - from) % 360) + 540) % 360) - 180;
}
/**
 * Die Winkel einer Folge so aufgerollt, dass jede Strecke den kurzen Weg
 * nimmt: aus 0°, 357°, 3° wird 0°, −3°, +3°. Interpoliert wird auf dieser
 * Reihe -- die Ebene steht am Keyframe genau so, wie sie soll, dreht sich
 * dazwischen aber nicht mehr im Kreis.
 */
function unrolled(keys) {
    const out = [];
    keys.forEach((k, i) => {
        out.push(i === 0 ? k.transform.rotate : out[i - 1] + shortestTurn(keys[i - 1].transform.rotate, k.transform.rotate));
    });
    return out;
}
/**
 * Der nächste Keyframe vor oder nach `local`, in Sekunden des Clips.
 *
 * Für die Sprünge von Keyframe zu Keyframe: ohne sie muss man den Abspielkopf
 * von Hand suchen, und bei zwei Rauten, die drei Bilder auseinanderliegen,
 * trifft man ihn nicht.
 */
export function keyframeAround(clip, local, direction, epsilon = 1e-4) {
    const times = sorted(clip).map((k) => k.at);
    const candidates = direction === 1 ? times.filter((t) => t > local + epsilon) : times.filter((t) => t < local - epsilon).reverse();
    return candidates[0] ?? null;
}
/** Steht auf `local` schon ein Keyframe? Dann setzt der Knopf ihn nicht neu, sondern weg. */
export function keyframeAt(clip, local, tolerance) {
    const found = sorted(clip).find((k) => Math.abs(k.at - local) < tolerance);
    return found ? found.at : null;
}
export function hasKeyframes(clip) {
    return clip.keyframes.length > 0;
}
/**
 * Welche Kurve an dieser Stelle gerade gilt (S30) -- die des letzten
 * Keyframes, der nicht hinter `local` liegt.
 *
 * Gebraucht beim Zuschneiden eines Abschnitts: der bekommt einen neuen
 * Keyframe an seinem Anfang, und der soll die Bewegung fortsetzen und nicht
 * eine neue anfangen.
 */
export function kurveBei(clip, local) {
    const keys = sorted(clip);
    if (keys.length === 0)
        return 'weich';
    let gilt = keys[0].kurve;
    for (const k of keys) {
        if (k.at <= local + 1e-9)
            gilt = k.kurve;
        else
            break;
    }
    return gilt;
}
/** The transform at `local` seconds into the clip. */
export function transformAt(clip, local) {
    const keys = sorted(clip);
    if (keys.length === 0)
        return clip.transform;
    const first = keys[0];
    const last = keys[keys.length - 1];
    if (local <= first.at)
        return { ...first.transform };
    if (local >= last.at)
        return { ...last.transform };
    const turns = unrolled(keys);
    for (let i = 0; i < keys.length - 1; i++) {
        const a = keys[i];
        const b = keys[i + 1];
        if (local >= a.at && local <= b.at) {
            const u = b.at === a.at ? 1 : kurveWert(a.kurve, (local - a.at) / (b.at - a.at));
            const mix = (from, to) => from + (to - from) * u;
            return {
                x: mix(a.transform.x, b.transform.x),
                y: mix(a.transform.y, b.transform.y),
                scale: mix(a.transform.scale, b.transform.scale),
                rotate: mix(turns[i], turns[i + 1]),
                opacity: mix(a.transform.opacity, b.transform.opacity),
            };
        }
    }
    return { ...last.transform };
}
/**
 * The same path as an ffmpeg expression in the variable `tVar` (the clip's
 * own seconds -- the caller passes `t` for a filter that runs on the clip
 * segment, or `t-<start>` for an overlay in timeline time). Null when the
 * field does not change, so no per-frame evaluation is paid for nothing.
 */
export function keyframeExpression(clip, field, tVar) {
    const keys = sorted(clip);
    if (keys.length === 0)
        return null;
    // Beim Drehen wird auf der aufgerollten Reihe gerechnet, damit der Export
    // denselben kurzen Weg nimmt wie die Vorschau.
    const values = field === 'rotate' ? unrolled(keys) : keys.map((k) => k.transform[field]);
    if (values.every((v) => v === values[0]))
        return null;
    const v = (n) => fmt(n);
    const T = `(${tVar})`;
    // Build from the end: hold the last value, then wrap each segment in an if.
    let expr = v(values[values.length - 1]);
    for (let i = keys.length - 2; i >= 0; i--) {
        const a = keys[i];
        const b = keys[i + 1];
        const span = Math.max(b.at - a.at, 1e-6);
        const u = `clip((${T}-${v(a.at)})/${v(span)},0,1)`;
        const s = kurveAusdruck(a.kurve, u);
        const segment = `(${v(values[i])}+(${v(values[i + 1])}-${v(values[i])})*${s})`;
        expr = `if(lt(${T},${v(b.at)}),${segment},${expr})`;
    }
    // Before the first keyframe the first value holds.
    return `if(lt(${T},${v(keys[0].at)}),${v(values[0])},${expr})`;
}
/** Which fields actually move, for callers that build filters only when needed. */
export function animatedFields(clip) {
    return FIELDS.filter((f) => keyframeExpression(clip, f, 't') !== null);
}
/**
 * Whether the clip is anywhere but where a clip starts out: moved, scaled,
 * turned, or animated. A base-track video that is not is simply the frame;
 * one that is has to be composed onto a background like an overlay.
 */
export function movesOnCanvas(clip) {
    if (clip.keyframes.length > 0)
        return true;
    const t = clip.transform;
    return Math.abs(t.x - 0.5) > 1e-6 || Math.abs(t.y - 0.5) > 1e-6 || Math.abs(t.scale - 1) > 1e-6 || Math.abs(t.rotate) > 1e-6;
}
//# sourceMappingURL=keyframes.js.map